import { Injectable } from '@angular/core';
import {
  Outcome,
  OutcomeOperator,
  Widget,
  WidgetComponent,
  WidgetComponentSubType,
  WidgetComponentType,
  WidgetState,
} from '../widgets.model';
import WidgetComponentHelpers from '../widget-component-helpers';
import { LanguageService } from '../../core/language.service';

@Injectable()
export class OutcomeLogicService {
  constructor(private readonly languageService: LanguageService) {}

  public getOutcomeDisplayName(outcome: Outcome, index, inputComponents: any[]): string {
    if (outcome.name) {
      return outcome.name;
    }
    if (outcome.answers.length === 0) {
      return outcome.isDefault
        ? this.languageService.translateInstantly('campaign.outcomes.default-outcome')
        : `Outcome ${index + 1}`;
    }
    return outcome.answers
      .map((a, index) => {
        let question = a.question;
        const inputDisplayName = inputComponents.find((x) => x.name === question)?.displayName;
        if (inputDisplayName) {
          question = inputDisplayName;
        }
        const operatorText = this.translateOperator(a.operator);
        const valueText = Array.isArray(a.value)
          ? a.value.join(', ')
          : (() => {
              try {
                const parsed = JSON.parse(a.value);
                return Array.isArray(parsed) ? parsed.join(', ') : a.value;
              } catch {
                return a.value;
              }
            })();

        const prefix = index > 0 && a.andOrOperator ? ` ${a.andOrOperator.toLowerCase()} ` : '';

        return `${prefix}${question} ${operatorText} ${valueText}`;
      })
      .join('');
  }

  private translateOperator(operator: OutcomeOperator): string {
    return this.languageService.translateInstantly('campaign.outcomes.operators.' + operator);
  }

  public getInputComponents(widget: Widget) {
    const componentData: {
      name: string;
      displayName: string;
      values: string[];
      valuesWithScore: { value: string; score: number; isCorrect: boolean }[];
      isMultiSelect: boolean;
    }[] = [];
    const sortedStates = [...widget.states].sort((a, b) => a.order - b.order);
    let stepCounter = 0;

    for (const state of sortedStates) {
      if (state.type === WidgetComponentType.Regular) {
        stepCounter++;
      }

      const stateComponents = this.getComponentsOnState(state);
      const inputComponents = stateComponents.filter((component) => this.isInputFormField(component));

      for (const component of inputComponents) {
        let name: string;
        let displayName: string;

        if (component.name) {
          name = component.name;
          displayName = component.label?.text || component.name;
        } else {
          // Built-in Components with no name field
          switch (component.subType) {
            case WidgetComponentSubType.InputTextEmail:
              name = 'email';
              displayName = component.label?.text || 'email';
              break;
            case WidgetComponentSubType.InputTextName:
              name = 'name';
              displayName = component.label?.text || 'name';
              break;
            case WidgetComponentSubType.InputTextFirstName:
              name = 'firstname';
              displayName = component.label?.text || 'firstname';
              break;
            case WidgetComponentSubType.InputTextLastName:
              name = 'lastname';
              displayName = component.label?.text || 'lastname';
              break;
            case WidgetComponentSubType.InternationalPhoneRegular:
            case WidgetComponentSubType.InputTextPhone:
              name = 'telephone';
              displayName = component.label?.text || 'telephone';
              break;
            default:
              continue;
          }
        }

        if (state.type === WidgetComponentType.Regular) {
          displayName = `Step ${stepCounter}: ${displayName}`;
        }

        // Add listItems to value-dropdown
        const values: string[] = [];
        const valuesWithScore: { value: string; score: number; isCorrect: boolean }[] = [];
        if (component.listItems?.length > 0) {
          values.push(...component.listItems.map((item) => item.key));
          valuesWithScore.push(
            ...component.listItems.map((item) => ({
              value: item.key,
              score: item.score || 0,
              isCorrect: item.isCorrect || false,
            })),
          );
        }

        let isMultiSelect = false;
        if (component.type === WidgetComponentType.ButtonList) {
          if (component.subType === WidgetComponentSubType.ButtonListCheckbox) {
            isMultiSelect = true;
          } else if (component.subType === WidgetComponentSubType.ButtonListRadio) {
            isMultiSelect = false;
          } else {
            isMultiSelect = component.buttonListProperties?.allowMultipleSelection || false;
          }
        }

        // Add component to question-dropdown
        componentData.push({ name, displayName, values, valuesWithScore, isMultiSelect });
      }
    }

    // Add score to question-dropdown
    const allComponents = WidgetComponentHelpers.getAllComponentsOnWidget(widget);
    const hasScore = allComponents.some((c) => c.listItems?.some((item) => item.score > 0));
    if (hasScore) {
      componentData.push({
        name: 'score',
        displayName: 'Score',
        values: [],
        valuesWithScore: [],
        isMultiSelect: false,
      });
    }

    return componentData;
  }

  private getComponentsOnState(state: WidgetState): WidgetComponent[] {
    const components: WidgetComponent[] = [];
    if (state.containers) {
      state.containers.forEach((container) => {
        if (container.components) {
          container.components.forEach((component) => {
            components.push(component);
            if (component.children) {
              component.children.forEach((child) => {
                components.push(child);
                if (child.children?.length > 0) {
                  child.children.forEach((nestedChild) => {
                    components.push(nestedChild);
                  });
                }
              });
            }
          });
        }
      });
    }
    if (state.floatingContainer && state.floatingContainer.components) {
      components.push(...state.floatingContainer.components);
    }
    return components;
  }

  private isInputFormField(component: WidgetComponent): boolean {
    const inputFormFieldTypes: WidgetComponentType[] = [
      WidgetComponentType.InputText,
      WidgetComponentType.ButtonList,
      WidgetComponentType.Dropdown,
    ];
    return inputFormFieldTypes.includes(component.type);
  }
}
